/**
 * Labstep
 *
 * @module components/Comment/Form/Create
 * @desc Reply bar for comments
 */

import { AttachmentMenu } from 'labstep-web/components/Comment/AttachmentMenu';
import { CanCreate } from 'labstep-web/components/Entity/Can';
import { FileActionUploadContainer as FileActionUpload } from 'labstep-web/components/File/Action/Upload';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import type { EntityCreateContainerChildrenProps } from 'labstep-web/containers/Entity/Create/types';
import { Button } from 'labstep-web/core/Button';
import Mentions from 'labstep-web/core/Mentions';
import type {
  APICallOptions,
  EntityClassForName,
} from 'labstep-web/models';
import { Comment } from 'labstep-web/models/comment.model';
import type { EntityCreatePayload } from 'labstep-web/services/entity-payload-types';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import React from 'react';
import styles from './styles.module.scss';
import type {
  ICommentFormCreateCreateContainerProps,
  ICommentFormCreateProps,
  ICommentFormCreateState,
} from './types';

export class CommentFormCreate extends React.Component<
  ICommentFormCreateProps,
  ICommentFormCreateState
> {
  static defaultProps = {
    placeholder: 'Reply with a comment',
  };

  constructor(props: ICommentFormCreateProps) {
    super(props);
    this.state = {
      commentText: '',
    };
    this.handleCreateComment = this.handleCreateComment.bind(this);
    this.handleCreateAttachment =
      this.handleCreateAttachment.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleCreateComment(evt: React.MouseEvent | React.KeyboardEvent) {
    evt.preventDefault();
    const { create, onSuccess, threadId } = this.props;

    const { commentText } = this.state;
    if (commentText === '') {
      return;
    }

    create(
      { parent_thread_id: threadId, body: commentText },
      { onSuccess },
    );

    this.setState({ commentText: '' }); // Clear Input
  }

  handleCreateAttachment(
    body: EntityCreatePayload<EntityClassForName<'comment'>>,
    options: APICallOptions = {},
  ) {
    const { create, onSuccess, threadId } = this.props;
    create(
      { body: '', ...body, parent_thread_id: threadId },
      enhanceOptions({ options, onSuccess }),
    );
  }

  handleInputChange(value: string) {
    this.setState({ commentText: value });
  }

  handleKeyDown(evt: React.KeyboardEvent) {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      this.handleCreateComment(evt);
    }
  }

  render() {
    const { commentText } = this.state;
    const { status, autoFocus } = this.props;

    return (
      <div data-cy="components/Comment/Form/Create">
        <div onKeyDown={this.handleKeyDown}>
          <div className={styles.container}>
            <AttachmentMenu
              create={this.handleCreateAttachment}
              status={status}
            />
            <div className={styles.textAreaContainer}>
              <FileActionUpload
                showDragEnter
                multiple
                noClick
                options={{
                  onSuccess: ({ response }) => {
                    if (!response) {
                      return;
                    }
                    this.handleCreateAttachment({
                      body: ' ',
                      file_id: [response.result as number | string],
                    });
                  },
                }}
              >
                <Mentions
                  value={commentText}
                  onChange={this.handleInputChange}
                  placeholder="Type @ to mention someone"
                  autoFocus={autoFocus}
                />
              </FileActionUpload>
            </div>
            <div className={styles.sendButtonContainer}>
              <Button
                dataCy="components/Comment/Form/Create:send"
                primary
                disabled={(status && status.isFetching) || false}
                onClick={this.handleCreateComment}
                icon="send"
                type="submit"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const CommentFormCreateCreateContainer: React.FC<
  ICommentFormCreateCreateContainerProps
> = (props) => (
  <CanCreate
    parentName="thread"
    parentId={props.threadId}
    entityName="comment"
  >
    <EntityCreateContainer entityName={Comment.entityName}>
      {(
        containerProps: EntityCreateContainerChildrenProps<Comment>,
      ) => <CommentFormCreate {...containerProps} {...props} />}
    </EntityCreateContainer>
  </CanCreate>
);
